import axios from 'axios';
import { waitForCourseAccessWrites } from './courseRecency';

// Create axios instance with base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api'
});

// Add token to requests if available
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  return config;
}, error => {
  return Promise.reject(error);
});

// Handle response errors
api.interceptors.response.use(
  response => response,
  error => {
    // Handle token expiration — but NEVER yank the user to /login on a
    // 401 that came from the auth endpoints themselves. A wrong password
    // or wrong OTP returns 401, and force-reloading there made the user
    // lose their current login tab + retyped identifier + remaining OTP
    // attempts. Let those errors bubble up so the form can show "wrong
    // password" / "wrong code, N attempts left" inline.
    if (error.response && error.response.status === 401) {
      const url = (error.config?.url || '').toLowerCase();
      const isAuthCall = url.includes('/auth/login')
                      || url.includes('/auth/otp')
                      || url.includes('/auth/verify')
                      || url.includes('/auth/forgot')
                      || url.includes('/auth/reset');
      // Also don't bounce if we're already sitting on the login page —
      // the user is mid-login and a reload would just wipe their form.
      const onLoginPage = typeof window !== 'undefined'
        && window.location.pathname.startsWith('/login');
      // Public survey visitors must never be pulled into login, including
      // when a stale portal token triggers the provider's background /auth/me.
      const onPublicSurvey = typeof window !== 'undefined'
        && window.location.pathname.startsWith('/surveys/');
      if (!isAuthCall && !onLoginPage && !onPublicSurvey && !url.startsWith('/public/surveys/')) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Recycle Bin (soft-deleted items, 30-day retention)
export const diagnosticsService = {
  memory:   (opts = {})         => api.get('/admin/diagnostics/memory', { params: opts.gc ? { gc: 1 } : {} }),
  listLogs: ()                  => api.get('/admin/diagnostics/logs'),
  tailLog:  (name, lines = 200) => api.get(`/admin/diagnostics/logs/${encodeURIComponent(name)}/tail`, { params: { lines } }),
  // Downloading binary content — let the browser handle it via a direct
  // <a href> hit. We just expose the URL builder + a helper that uses
  // axios with responseType:'blob' so the bearer token gets attached.
  downloadLog: async (name) => {
    const res = await api.get(`/admin/diagnostics/logs/${encodeURIComponent(name)}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  }
};

export const recycleBinService = {
  summary:    ()                  => api.get('/recycle-bin'),
  list:       (type, params = {}) => api.get(`/recycle-bin/${type}`, { params }),
  restore:    (type, id)          => api.post(`/recycle-bin/${type}/${encodeURIComponent(id)}/restore`),
  releaseNationalId: (id)         => api.post(`/recycle-bin/users/${encodeURIComponent(id)}/release-national-id`),
  permanent:  (type, id)          => api.delete(`/recycle-bin/${type}/${encodeURIComponent(id)}`),
};

// Pricing protocol — official tuition price list (بروتوكول الرسوم الدراسية).
// superadmin/admin only. No POST/DELETE — configs are seeded; only edit.
// Family loyalty groups (households) — office-managed.
export const familyGroupService = {
  list: (q) => api.get('/family-groups', { params: q ? { q } : {} }),
  get: (id) => api.get(`/family-groups/${id}`),
  create: (data) => api.post('/family-groups', data),
  update: (id, data) => api.patch(`/family-groups/${id}`, data),
  addMember: (id, userId) => api.post(`/family-groups/${id}/members`, { userId }),
  removeMember: (id, userId) => api.delete(`/family-groups/${id}/members/${userId}`),
  remove: (id) => api.delete(`/family-groups/${id}`),
};

// Loyalty discounts (referrals + family) — office-facing summary/recompute.
export const loyaltyService = {
  getStudent: (id) => api.get(`/loyalty/student/${id}`),
  recompute: (id) => api.post(`/loyalty/student/${id}/recompute`),
  // Global overview for the pricing-protocol page: policy + families +
  // referrals + live loyalty rows + active price overrides.
  overview: () => api.get('/loyalty/overview'),
};

export const pricingProtocolService = {
  list:    ()            => api.get('/pricing-protocol'),
  update:  (id, data)    => api.put(`/pricing-protocol/${id}`, data),
  history: (id)          => api.get(`/pricing-protocol/${id}/history`),
  // Create (or reactivate) a cohort-specific copy of a program's bundle
  // pricing; id = the program's default bundle doc, cloned server-side.
  createCohort: (id, cohortId, data) => api.post(`/pricing-protocol/${id}/cohort/${cohortId}`, data),
};

// College agreements (terms a user must accept before using the portal).
// Office endpoints manage the agreements; the gate endpoints are used by the
// AgreementGate to fetch + record what the current user still has to accept.
export const agreementService = {
  // Office management (superadmin/admin)
  list:    ()        => api.get('/agreements'),
  create:  (data)    => api.post('/agreements', data),
  update:  (id, data) => api.put(`/agreements/${id}`, data),
  remove:  (id)      => api.delete(`/agreements/${id}`),
  // End-user acceptance gate (any authenticated user)
  pending: ()        => api.get('/agreements/pending'),
  accept:  (agreementIds) => api.post('/agreements/accept', { agreementIds }),
};

// Auth services
export const authService = {
  // Login with username and password.
  // `rememberMe=true` asks the server for a 30-day JWT instead of 24h.
  login: (username, password, rememberMe = false) => {
    return api.post('/auth/login', { username, password, rememberMe });
  },
  
  // Logout and invalidate token
  logout: () => {
    return api.post('/auth/logout');
  },
  
  // Get current user info
  getCurrentUser: () => {
    return api.get('/auth/me');
  },
  
  // Change password
  changePassword: (currentPassword, newPassword) => {
    return api.post('/auth/change-password', { 
      currentPassword, 
      newPassword 
    });
  },
  
  // Update user preferences
  updatePreferences: (preferences) => {
    return api.put('/auth/preferences', { preferences });
  },

  // Update user profile. Any user can self-edit name, phone, city and
  // language. idNumber and gender are staff-only. An email change is NOT
  // applied immediately — the server parks it and mails a confirmation code
  // to the new address (see verifyEmailChange), returning
  // `emailVerificationRequired` + `pendingEmail`.
  updateProfile: (profileData) => {
    return api.put('/auth/profile', profileData);
  },

  // Confirm a pending email change with the code mailed to the new address.
  verifyEmailChange: (code) => {
    return api.post('/auth/email/verify', { code });
  },

  // Abandon a pending email change.
  cancelEmailChange: () => {
    return api.post('/auth/email/cancel');
  },

  // Upload / replace current user's avatar. Expects a File or Blob.
  uploadAvatar: (file) => {
    const fd = new FormData();
    fd.append('avatar', file);
    return api.post('/auth/avatar', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Request one-time login code via email
  requestOtp: (identifier) => {
    return api.post('/auth/otp/request', { identifier });
  },

  // Verify one-time login code and receive a JWT.
  // `rememberMe=true` asks the server for a 30-day JWT.
  verifyOtp: (identifier, code, rememberMe = false) => {
    return api.post('/auth/otp/verify', { identifier, code, rememberMe });
  },

  // Request password-reset code via email
  forgotPassword: (identifier) => {
    return api.post('/auth/password/forgot', { identifier });
  },

  // Reset password with the emailed code.
  // Accepts an object so callers can name args clearly.
  resetPassword: ({ identifier, code, newPassword }) => {
    return api.post('/auth/password/reset', { identifier, code, newPassword });
  }
};

// Courses (the new Course model with routing tags + auto-provisioning)
export const courseService = {
  list:       async (params = {}) => {
    if (params.sort === 'lastAccessed') await waitForCourseAccessWrites();
    return api.get('/courses', { params });
  },
  getStats:   () => api.get('/courses/stats/summary'),
  get:        (id) => api.get(`/courses/${id}`),
  create:     (data) => api.post('/courses', data),
  update:     (id, data) => api.put(`/courses/${id}`, data),
  remove:     (id) => api.delete(`/courses/${id}`),
  move:       (id, data) => api.patch(`/courses/${id}/move`, data),
  provision:  (id) => api.post(`/courses/${id}/provision`),
  endZoom:    (id) => api.post(`/courses/${id}/end-zoom`),
};

// Programs (root courses) — manages program shell, hierarchy template, cohorts
export const programService = {
  list:                (params = {}) => api.get('/programs', { params }),
  knownHosts:          () => api.get('/programs/known-hosts'),
  get:                 (id) => api.get(`/programs/${id}`),
  create:              (data) => api.post('/programs', data),
  update:              (id, data) => api.put(`/programs/${id}`, data),
  remove:              (id) => api.delete(`/programs/${id}`),
  updateHierarchy:     (id, hierarchyTemplate) => api.put(`/programs/${id}/hierarchy`, { hierarchyTemplate }),
  addCohort:           (id, cohort) => api.post(`/programs/${id}/cohorts`, cohort),
  updateCohort:        (id, cohortId, cohort) => api.put(`/programs/${id}/cohorts/${cohortId}`, cohort),
  removeCohort:        (id, cohortId) => api.delete(`/programs/${id}/cohorts/${cohortId}`),
};

// Zoom -> Google Drive backup
export const zoomMeetingService = {
  list: (params = {}) => api.get('/zoom/meetings', { params }),
  scan: (meetingId) => api.post(`/zoom/meetings/${meetingId}/scan`),
  mirrorBunny: (meetingId) => api.post(`/zoom/meetings/${meetingId}/mirror-bunny`),
  syncFromDrive: (meetingId) => api.post(`/zoom/meetings/${meetingId}/sync-from-drive`),
};

export const auditLogService = {
  list:   (params = {}) => api.get('/audit-logs', { params }),
  facets: ()            => api.get('/audit-logs/facets'),
};

export const courseCopyFileIncidentService = {
  listOpen: () => api.get('/admin/course-copy-file-incidents'),
  resolve: (id, note = '') => api.post(`/admin/course-copy-file-incidents/${id}/resolve`, { note }),
};

// RBAC/ABAC visual management (صفحة الصلاحيات والأدوار) — superadmin/admin/manager.
export const rbacService = {
  getOverview: () => api.get('/rbac/overview'),
  updateRolePermissions: (roleId, payload) => api.patch(`/rbac/roles/${roleId}/permissions`, payload),
  getOperations: () => api.get('/rbac/operations'),
  getMyOperations: () => api.get('/rbac/operations/mine'),
  updateOperations: (roleId, payload) => api.patch(`/rbac/operations/${roleId}`, payload),
};

// WhatsApp template catalog (قوالب واتساب) — list for compose picker, manage for office.
export const whatsappTemplateService = {
  list: () => api.get('/whatsapp-templates'),
  create: (payload) => api.post('/whatsapp-templates', payload),
  remove: (name) => api.delete(`/whatsapp-templates/${encodeURIComponent(name)}`),
};

export const zoomBackupService = {
  getStatus: () => api.get('/zoom-backup/status'),
  getRecordings: (params = {}) => api.get('/zoom-backup/recordings', { params }),
  getDiskFiles: () => api.get('/zoom-backup/disk-files'),
  runNow: () => api.post('/zoom-backup/run'),
  retry: (id) => api.post(`/zoom-backup/recordings/${id}/retry`),
  runFullSync: () => api.post('/zoom-backup/full-sync'),
  getFullSyncStatus: () => api.get('/zoom-backup/full-sync/status'),
  // Incomplete local-recording uploads detected from recording.conf files in Drive.
  getRecordingIncidents: (params = {}) => api.get('/admin/recording-incidents', { params }),
  getRecordingIncidentDiagnostics: (id) => api.get(`/admin/recording-incidents/${id}/diagnostics`),
  dismissRecordingIncident: (id, payload) => api.post(`/admin/recording-incidents/${id}/dismiss`, payload),
  previewRecordingRestore: (id) => api.post(`/admin/recording-incidents/${id}/restore-preview`),
  restoreRecordingIncident: (id, operationId) => api.post(`/admin/recording-incidents/${id}/restore`, { operationId, confirmed: true }),
  // Stranded cloud recordings — Zoom cloud media the portal has no row for.
  getStranded: (params = {}) => api.get('/admin/recording-incidents/stranded', { params }),
  dismissStranded: (id, payload) => api.post(`/admin/recording-incidents/stranded/${id}/dismiss`, payload),
};

// Nightly/background job run status (admin) — powers /admin/job-status.
export const jobRunService = {
  getStatus: () => api.get('/admin/job-runs/status'),
  // Manually re-run a nightly/background job from the status page. Recorded
  // server-side as triggeredBy:'manual'.
  runNow: (jobKey) => api.post(`/admin/job-runs/${jobKey}/run`),
};

export const driveAccessGrantService = {
  listFailed: () => api.get('/admin/drive-access-grants'),
  retry: (id) => api.post(`/admin/drive-access-grants/${id}/retry`),
  acknowledge: (id) => api.delete(`/admin/drive-access-grants/${id}`),
};

// Drive → Bunny mirror admin (per-program / per-course / all).
// Powers the /admin/bunny-mirror page (BunnyMirror.jsx).
export const bunnyMirrorService = {
  getStatus:       () => api.get('/course-recordings/admin/bunny-mirror/status'),
  getScopeOptions: () => api.get('/course-recordings/admin/bunny-mirror/scope-options'),
  getVideos:       (params = {}) => api.get('/course-recordings/admin/bunny-mirror/videos', { params }),
  // scope: { all: true } | { programIds: [...] } | { cohortIds: [...] } | { courseIds: [...] } | null (= pilot default)
  runNow:          (scope = null) => api.post('/course-recordings/admin/bunny-mirror/run', { scope }),
  runPlaybackAudit: () => api.post('/course-recordings/admin/bunny-mirror/playback-audit'),
  retry:           (id) => api.post(`/course-recordings/admin/bunny-mirror/videos/${id}/retry`),
  // On-demand: walk Drive for one course and return live file list with bunny status.
  scanCourse:      (courseId) => api.get(`/course-recordings/admin/bunny-mirror/scan/${courseId}`),
};

// Admin settings (integrations status + security activity log)
export const adminSettingsService = {
  getIntegrationsStatus: () => api.get('/admin/integrations/status').then((r) => r.data),
  sendTestEmail: (to) => api.post('/admin/integrations/test-email', { to }).then((r) => r.data),
  getSecurityActivity: (limit = 15) =>
    api.get('/admin/security/activity', { params: { limit } }).then((r) => r.data),
  getWhatsappSettings: () => api.get('/admin/whatsapp').then((r) => r.data),
  saveWhatsappSettings: (payload) => api.put('/admin/whatsapp', payload).then((r) => r.data),
  sendTestWhatsapp: (to) => api.post('/admin/integrations/test-whatsapp', { to }).then((r) => r.data),
};

// Admin services
export const adminService = {
  // Get statistics
  getStats: () => {
    return api.get('/admin/stats');
  },
  
  // Get all users
  getUsers: () => {
    return api.get('/users');
  },
  
  // Create user
  createUser: (userData) => {
    return api.post('/users', userData);
  },
  
  // Update user
  updateUser: (id, userData) => {
    return api.put(`/users/${id}`, userData);
  },
  
  // Delete user
  deleteUser: (id) => {
    return api.delete(`/users/${id}`);
  },

  // Reset user password (role hierarchy enforced)
  resetUserPassword: (userId, newPassword = null) => {
    return api.post(`/users/${userId}/reset-password`, { newPassword });
  },

  // Institution-wide weekly schedule — every active course with a
  // dayOfWeek+startTime, across every teacher. Powers the office/admin
  // "جدول المحاضرات" page so non-teacher staff can see what's running.
  getGlobalSchedule: () => api.get('/admin/global-schedule'),

};

// ------------------------------------------------------------
// Shared Kanban task board ("لوحة المهام") — admin/staff/superadmin
// ------------------------------------------------------------
export const taskBoardService = {
  list: () => api.get('/tasks'),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.patch(`/tasks/${id}`, data),
  move: (id, status, beforeId = null) => api.post(`/tasks/${id}/move`, { status, beforeId }),
  remove: (id) => api.delete(`/tasks/${id}`)
};

// ------------------------------------------------------------
// Native LMS — student / teacher course list calls and the gradebook.
// ------------------------------------------------------------

export const myCoursesService = {
  list: async () => {
    await waitForCourseAccessWrites();
    return api.get('/my-courses');
  },
  markAccess: (courseId) => api.post(`/my-courses/${courseId}/access`, {}, { timeout: 5000 }),
  schedule: () => api.get('/my-schedule'),
  // Fetch a fresh Zoom host start_url so a teacher can launch the meeting as host.
  zoomHostUrl: (courseId) => api.get(`/my-courses/${courseId}/zoom-host`),
  // Aggregated course summary for the tabbed course shell (header + badges).
  overview: (courseId) => api.get(`/my-courses/${courseId}/overview`),
  // Set/clear the Overview announcement note (teacher/elevated only).
  setAnnouncement: (courseId, text) =>
    api.patch(`/my-courses/${courseId}/announcement`, { text })
};

export const enrollmentService = {
  listForCourse: (courseId, status = 'active') =>
    api.get(`/enrollments/course/${courseId}`, { params: { status } }),
  availableForCourse: (courseId, params = {}) =>
    api.get(`/enrollments/course/${courseId}/available`, { params }),
  mine: () => api.get('/enrollments/me'),
  bulkAssign: (courseId, userIds) =>
    api.post('/enrollments', { courseId, userIds }, { params: { impact: 1 } }),
  drop: (courseId, userId, reason = '') =>
    api.delete('/enrollments', { data: { courseId, userId, reason }, params: { impact: 1 } }),
  // Admin: list a single user's enrolments (any status: active|dropped|completed|all)
  listForUser: (userId, status = 'active', { includePrograms = false } = {}) =>
    api.get(`/enrollments/user/${userId}`, { params: { status, ...(includePrograms ? { includePrograms: 1 } : {}) } }),
  // Admin: drop a student from many courses at once with a shared reason
  bulkDrop: (userId, courseIds, reason = '') =>
    api.post('/enrollments/bulk-drop', { userId, courseIds, reason }, { params: { impact: 1 } })
};

// Resolve a finance-impact token produced by roster mutations (?impact=1).
// The server blocks briefly while payment-plan syncing settles, then returns
// the per-student before/after finance diff.
export const financeImpactService = {
  get: (token) => api.get(`/finance-impact/${token}`)
};

// All enrolments for cohort members across linked courses (any status).
// Used by the admin Cohort Detail page tabs.
export const cohortDetailService = {
  listEnrollments: (cohortId, status = 'active') =>
    api.get(`/cohorts/${cohortId}/enrollments`, { params: { status } }),
  getMembers: (cohortId) => api.get(`/cohorts/${cohortId}/members`),
  getCourses: (cohortId) => api.get(`/cohorts/${cohortId}/courses`)
};

// Cohort-level admin actions. `cloneFrom` creates the next cohort of a program
// by cloning a source cohort's courses (and optionally their content). It is
// long-running — it provisions Zoom + Drive and deep-copies files — so it
// overrides the axios timeout to 0 (no client-side abort).
export const cohortService = {
  // body: { cohort?:{name,startDate,endDate,status,durationYears,number?},
  //         courseFilter?:{courseNumbers:[...]}, year?, seedContent?, autoProvision? }
  cloneFrom: (sourceCohortId, body = {}) =>
    api.post(`/cohorts/clone-from/${sourceCohortId}`, body, { timeout: 0 }),
};

// Minimal attendance API.
export const attendanceService = {
  listSessions: (courseId) => api.get(`/attendance/course/${courseId}/sessions`),
  createSession: (courseId, data) => api.post(`/attendance/course/${courseId}/sessions`, data),
  getSession: (sessionId) => api.get(`/attendance/session/${sessionId}`),
  updateSession: (sessionId, data) => api.patch(`/attendance/session/${sessionId}`, data),
  deleteSession: (sessionId) => api.delete(`/attendance/session/${sessionId}`)
};

export const dashboardService = {
  getMine: () => api.get('/dashboard/me'),
};

export const teacherDashboardService = {
  getPendingGrading: () => api.get('/assignments/teacher/pending-grading'),
};

export const assignmentService = {
  publicationCapabilities: () => api.get('/assignments/publication-capabilities'),
  // courseId is the native Course _id (24-hex).
  list: (courseId, params = {}) => api.get(`/assignments/course/${courseId}`, { params }),
  create: (courseId, data) => api.post(`/assignments/course/${courseId}`, data),
  update: (id, data) => api.patch(`/assignments/${id}`, data),
  publication: (id, state) => api.post(`/assignments/${id}/publication`, { state }),
  // Quick "add N minutes" extension — bumps both timeLimitMinutes
  // (per-attempt countdown) and dueAt (late-submit cutoff) by the same
  // amount. Safe while the exam is live: every student in the attempt
  // picks up the extra time on their next save/submit.
  extend: (id, minutes) => api.post(`/assignments/${id}/extend`, { minutes }),
  remove: (id) => api.delete(`/assignments/${id}`),
  uploadAttachment: (id, formData) => api.post(`/assignments/${id}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  attachmentUrl: (assignmentId, attachmentId, dl = false) =>
    `/api/assignments/${assignmentId}/attachments/${attachmentId}/download${dl ? '?dl=1' : ''}`,
  myDeadlines: (days = 30) => api.get('/my-deadlines', { params: { days } })
};

export const submissionService = {
  mine: (assignmentId) => api.get(`/submissions/assignment/${assignmentId}/mine`),
  listForAssignment: (assignmentId) => api.get(`/submissions/assignment/${assignmentId}`),
  submit: (assignmentId, formData) => api.post(`/submissions/assignment/${assignmentId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  grade: (submissionId, data) => api.post(`/submissions/${submissionId}/grade`, data),
  fileUrl: (submissionId, fileId, dl = false) =>
    `/api/submissions/${submissionId}/files/${fileId}/download${dl ? '?dl=1' : ''}`
};

export const registrationCompletionService = {
  get: (id) => api.get(`/leads/${encodeURIComponent(id)}/completion-link`),
  issue: (id) => api.post(`/leads/${encodeURIComponent(id)}/completion-link`),
  revoke: (id) => api.delete(`/leads/${encodeURIComponent(id)}/completion-link`),
  open: (token) => fetch('/api/public/registration-completion/open', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    credentials: 'omit', referrerPolicy: 'no-referrer', cache: 'no-store',
    body: JSON.stringify({ token }),
  }).then(r => r.json()),
  submit: (token, answers, requestId) => fetch('/api/public/registration-completion/submit', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    credentials: 'omit', referrerPolicy: 'no-referrer', cache: 'no-store',
    body: JSON.stringify({ token, answers, requestId }),
  }).then(r => r.json()),
};

export const registrationFormService = {
  channelStats: (params = {}) => api.get('/registration-forms/channel-stats', { params }),
  channelFormOptions: () => api.get('/registration-forms/channel-form-options'),
  list:        (params = {}) => api.get('/registration-forms', { params }),
  get:         (id)       => api.get(`/registration-forms/${id}`),
  create:      (data)     => api.post('/registration-forms', data),
  update:      (id, data) => api.patch(`/registration-forms/${id}`, data),
  remove:      (id)       => api.delete(`/registration-forms/${id}`),
  duplicate:   (id)       => api.post(`/registration-forms/${id}/duplicate`),
  submissions: (id, params = {}) => api.get(`/registration-forms/${id}/submissions`, { params }),
  // Sets of program/course/cohort ids that currently have an active form —
  // drives the leads add-form + export pickers (form-driven visibility).
  activeTargets: () => api.get('/registration-forms/active-targets'),
  uploadHeroImage: (id, file) => {
    const fd = new FormData();
    fd.append('image', file);
    return api.post(`/registration-forms/${id}/hero-image`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  convertPreview: (id)         => api.get(`/registration-forms/${id}/convert-preview`),
  convertLeads:   (id, leadIds) => api.post(`/registration-forms/${id}/convert-leads`, leadIds ? { leadIds } : {}),
  // Public — no auth header needed. We deliberately use plain fetch to
  // bypass the api.js interceptor that auto-attaches a Bearer token.
  publicList:   ()           => fetch('/api/public/registration').then(r => r.json()),
  publicGet:    (slug)        => fetch(`/api/public/registration/${slug}`).then(r => r.json()),
  publicSubmit: (slug, data, query = '', clientRequestToken, registrationMode)  => fetch(`/api/public/registration/${encodeURIComponent(slug)}${query ? `?${query}` : ''}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data, ...(clientRequestToken ? { clientRequestToken } : {}), ...(registrationMode === 'initial' ? { registrationMode: 'initial' } : {}) }),
  }).then(r => r.json()),
};

export const gradeService = {
  examAverage: (courseId, scope = 'mine') => api.get(`/grades/course/${courseId}/exam-average`, { params: { scope } }),
  // courseId is the native Course _id (24-hex).
  gradebook: (courseId) => api.get(`/grades/course/${courseId}`),
  mine: (courseId) => api.get(`/grades/course/${courseId}/me`),
  myAll: () => api.get('/grades/my-all'),
  addManual: (courseId, data) => api.post(`/grades/course/${courseId}/manual`, data),
  // Quick-edit a single gradebook cell. data: { studentId, type:'assignment'|'manual',
  // assignmentId?, label?, maxScore?, bonusPoints?, weightPercent?, score } ('' clears).
  cell: (courseId, data) => api.put(`/grades/course/${courseId}/cell`, data),
  remove: (id) => api.delete(`/grades/${id}`),
  // Grading policy (per-course categories + weights)
  getPolicy: (courseId) => api.get(`/grades/course/${courseId}/policy`),
  savePolicy: (courseId, data) => api.put(`/grades/course/${courseId}/policy`, data),
  // Final-grade breakdown (uses gradingPolicyService.computeFinalGrade)
  breakdown: (courseId, studentId) =>
    api.get(`/grades/course/${courseId}/breakdown`, studentId ? { params: { studentId } } : undefined),
  myBreakdown: (courseId) => api.get(`/grades/course/${courseId}/my-breakdown`),
  // Grade release — notify students of their final grade.
  releaseStatus: (courseId) => api.get(`/grades/course/${courseId}/release-status`),
  release: (courseId) => api.post(`/grades/course/${courseId}/release`),
  releaseOne: (courseId, studentId) => api.post(`/grades/course/${courseId}/release/${studentId}`)
};

// Academic grade reports. PDF files must go through the authenticated axios
// client (rather than a direct link) so the bearer token is included.
const downloadAuthenticatedBlob = async (path, fallbackFilename, type = 'application/pdf', params) => {
  const res = await api.get(path, { responseType: 'blob', params });
  const blob = new Blob([res.data], { type: res.data?.type || type });
  const disposition = res.headers?.['content-disposition'] || '';
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plainName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  let filename = fallbackFilename;
  try {
    filename = encodedName ? decodeURIComponent(encodedName) : (plainName || fallbackFilename);
  } catch {
    filename = fallbackFilename;
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export const gradeReportService = {
  optionsMine: () => api.get('/grades/report/options'),
  optionsStudent: (studentId) => api.get(`/grades/report/student/${encodeURIComponent(studentId)}/options`),
  downloadMine: (params) =>
    downloadAuthenticatedBlob('/grades/report/pdf', 'almadina-grade-report.pdf', 'application/pdf', params),
  downloadStudent: (studentId, params) =>
    downloadAuthenticatedBlob(
      `/grades/report/student/${encodeURIComponent(studentId)}/pdf`,
      `almadina-grade-report-${studentId}.pdf`,
      'application/pdf',
      params
    ),
};

// Course-completion certificate (الإجازة). Teacher endpoints are gated server
// side (ELEVATED + can-manage); student endpoints resolve from the JWT.
export const certificateService = {
  // Teacher / admin
  status: (courseId) => api.get(`/certificates/course/${courseId}/status`),
  getTemplate: (courseId) => api.get(`/certificates/course/${courseId}/template`),
  saveTemplate: (courseId, data) => api.put(`/certificates/course/${courseId}/template`, data),
  preview: (courseId, studentId) =>
    api.get(`/certificates/course/${courseId}/preview`, { params: { studentId } }),
  issue: (courseId) => api.post(`/certificates/course/${courseId}/issue`),
  issueOne: (courseId, studentId) => api.post(`/certificates/course/${courseId}/issue/${studentId}`),
  // Student
  mine: () => api.get('/certificates/mine'),
  myCourseCert: (courseId) => api.get(`/certificates/course/${courseId}/me`),
};

// Manually-issued credentials (attendance/participation or graduation).
// Expected compact contract (kept here so endpoint assumptions stay centralized):
// GET  manual/metadata?studentId&type&contextType&contextId
//   -> { student,program?,course?,grade?,defaults,editableFields }
// POST manual/preview { studentId,type,contextType?,contextId?,fields } -> { html,fields? }
// POST manual         (same body) -> { item|certificate:{ _id,id,... } }
// GET  manual/:id/pdf -> PDF
// GET  manual/mine -> { items:[] }; GET manual/mine/:id -> { item|certificate,html }
// GET  manual/mine/:id/pdf -> PDF. The server remains authoritative for field
// limits, authorization, frozen HTML and PDF rendering.
export const certificateRetentionService = {
  preview: () => api.post('/certificates/retention/preview'),
  confirm: (token, confirmation) => api.post('/certificates/retention/confirm', { token, confirmation }),
};

export const manualCertificateService = {
  metadata: (studentId, params = {}) =>
    api.get('/certificates/manual/metadata', { params: { studentId, ...params } }),
  preview: (payload) => api.post('/certificates/manual/preview', payload),
  issue: (payload) => api.post('/certificates/manual', payload),
  issueBulk: (payload) => api.post('/certificates/manual/bulk', payload),
  copyDefaults: (type) => api.get('/certificates/manual/copy-defaults', { params: { type } }),
  saveCopyDefaults: (payload) => api.put('/certificates/manual/copy-defaults', payload),
  resetCopyDefaults: (type) => api.delete(`/certificates/manual/copy-defaults/${encodeURIComponent(type)}`),
  mine: () => api.get('/certificates/manual/mine'),
  mineOne: (id) => api.get(`/certificates/manual/mine/${encodeURIComponent(id)}`),
  downloadAdmin: (id, filename = `certificate-${id}.pdf`) =>
    downloadAuthenticatedBlob(`/certificates/manual/${encodeURIComponent(id)}/pdf`, filename),
  downloadMine: (id, filename = `certificate-${id}.pdf`) =>
    downloadAuthenticatedBlob(`/certificates/manual/mine/${encodeURIComponent(id)}/pdf`, filename),
};

export const financialChangeService = {
  list: (status = 'pending', options = {}) => api.get('/financial-change-requests', { params: { ...options, status } }),
  approve: (id) => api.post(`/financial-change-requests/${encodeURIComponent(id)}/approve`, {}),
  reject: (id, reason) => api.post(`/financial-change-requests/${encodeURIComponent(id)}/reject`, { reason }),
};

export const quizService = {
  gradingStatus: (id) => api.get(`/quiz/assignment/${id}/grading-status`),
  completeAnswerKey: (id, data) => api.post(`/quiz/questions/${id}/answer-key`, data),
  recomputeGrades: (id, reason) => api.post(`/quiz/assignment/${id}/recompute-grades`, { confirm: true, reason }),
  // Teacher
  listQuestions: (assignmentId) => api.get(`/quiz/assignment/${assignmentId}/questions`),
  createQuestion: (assignmentId, data) => api.post(`/quiz/assignment/${assignmentId}/questions`, data),
  updateQuestion: (questionId, data) => api.patch(`/quiz/questions/${questionId}`, data),
  removeQuestion: (questionId) => api.delete(`/quiz/questions/${questionId}`),
  gradeEssay: (submissionId, questionId, data) =>
    api.post(`/quiz/submission/${submissionId}/grade-essay/${questionId}`, data),
  // Student
  studentQuestions: (assignmentId) => api.get(`/quiz/assignment/${assignmentId}/questions/student`),
  start: (assignmentId) => api.post(`/quiz/assignment/${assignmentId}/start`),
  saveAnswer: (submissionId, questionId, data) =>
    api.patch(`/quiz/submission/${submissionId}/answer/${questionId}`, data),
  submit: (submissionId) => api.post(`/quiz/submission/${submissionId}/submit`),
  // Teacher preview ("test the exam yourself") — no Submission/Grade is written.
  previewQuestions: (assignmentId) => api.get(`/quiz/preview/${assignmentId}/questions`),
  previewGrade: (assignmentId, answers) => api.post(`/quiz/preview/${assignmentId}/grade`, { answers }),
  // Student post-submit review — only returns correctness if the teacher
  // released answers (assignment.answersReleasedAt != null). Otherwise the
  // response is { success:true, released:false }.
  myReview: (assignmentId) => api.get(`/quiz/assignment/${assignmentId}/my-review`),
};

// ------------------------------------------------------------
// Native student / teacher LMS services.
// These keep the old method names that the UI calls but proxy
// to native endpoints + reshape responses so JSX needs no changes.
// ------------------------------------------------------------

// Map a native Notification doc to the legacy shape the notifications pages
// expect: { id, category, date, title, message, read, source, url, priority,
// channels }. Legacy docs predate explicit channel selection; they used the
// model's historical in-app + push fan-out.
function _shapeNotificationForUi(n) {
  if (!n) return null;
  // Native `type` values: 'payment','system','announcement','assignment',
  // 'reminder',... — map to the UI's category buckets (payment/academic/system).
  const ACADEMIC = new Set(['assignment', 'grade', 'reminder', 'deadline', 'course']);
  const PAYMENT  = new Set(['payment', 'invoice', 'reminder_payment']);
  const t = String(n.type || '').toLowerCase();
  const category = PAYMENT.has(t) ? 'payment' : ACADEMIC.has(t) ? 'academic' : 'system';
  return {
    id: String(n._id || n.id),
    title: n.title || '',
    message: n.message || n.body || '',
    category,
    type: n.type,
    priority: n.priority || 'normal',
    read: !!n.read,
    date: n.createdAt || n.date,
    source: n.source || 'portal',
    url: n.link || n.url || null,
    channels: Array.isArray(n.channels) && n.channels.length
      ? n.channels
      : ['in_app', 'web_push', 'native_push'],
    raw: n,
  };
}

export const calendarEventService = {
  list:   (params)        => api.get('/calendar-events', { params }),
  mine:   (days = 30)     => api.get('/calendar-events/mine', { params: { days } }),
  linkableExams: (courseId) => api.get('/calendar-events/linkable-exams', { params: { courseId } }),
  create: (body)          => api.post('/calendar-events', body),
  update: (id, body)      => api.patch(`/calendar-events/${id}`, body),
  remove: (id)            => api.delete(`/calendar-events/${id}`),
  rsvp:   (id, status)    => api.post(`/calendar-events/${id}/rsvp`, { status }),
  // Re-send invitations (email + WhatsApp when enabled) to attendees who
  // haven't responded yet.
  resendInvites: (id)     => api.post(`/calendar-events/${id}/resend-invites`),
  // Internal people directory (office team + teachers) for the meeting
  // invitee picker — id/name/role/email only, office-tier callers only.
  // NOT tier-scoped: staff see admins/superadmin too (so anyone can invite
  // anyone), unlike the user-management list.
  directory: ()           => api.get('/calendar-events/directory'),
};

// Private calendars — personal office calendars with availability windows,
// a linked teacher's lectures, sharing, and free-slot booking.
export const privateCalendarService = {
  mine:       ()                    => api.get('/private-calendar/mine'),
  update:     (body)                => api.put('/private-calendar/mine', body),
  feed:       (days = 14)           => api.get('/private-calendar/mine/feed', { params: { days } }),
  addItem:    (body)                => api.post('/private-calendar/mine/items', body),
  removeItem: (id)                  => api.delete(`/private-calendar/mine/items/${id}`),
  shared:     ()                    => api.get('/private-calendar/shared'),
  freeBusy:   (ownerId, days = 14)  => api.get(`/private-calendar/${ownerId}/free-busy`, { params: { days } }),
  book:       (ownerId, body)       => api.post(`/private-calendar/${ownerId}/book`, body),
};

export const studentLmsService = {
  // /my-courses returns { teaching:[], enrolled:[] }. For a student that
  // means the courses live in `enrolled`. We flatten + reshape so the
  // dashboards keep getting `data:[ { id, fullname, shortname, ... } ]`.
  getCourses: async () => {
    const res = await api.get('/my-courses');
    const enrolled = res.data?.enrolled || [];
    const teaching = res.data?.teaching || [];
    const list = [...enrolled, ...teaching].map((c) => ({ ...c, id: c._id || c.id }));
    return { data: { success: true, data: list } };
  },
  // Aggregated gradebook across all the student's active courses.
  getGrades: () => api.get('/grades/my-all'),
  // Weekly schedule derived from Course (dayOfWeek/startTime/durationMinutes).
  getMySchedule: () => api.get('/my-schedule'),
  // Upcoming Assignment.dueAt across enrolled courses.
  getMyDeadlines: (days = 30) => api.get('/my-deadlines', { params: { days } }),
  // Caller's own identity (name/email/phone) used to stamp exported schedule
  // files. Scoped to req.user server-side — never leaks anyone else's contact.
  getMyScheduleIdentity: () => api.get('/my-schedule/identity'),
  // Weekly schedule as a real downloadable PDF (server-rendered). Fetched as a
  // blob through the authenticated client so the JWT travels in the header, then
  // saved via an <a download> so it lands as a file even on mobile (the old
  // window.print() path printed the live app page instead of the document).
  downloadSchedulePdf: async (lang = 'ar') => {
    const safeLang = lang === 'he' ? 'he' : 'ar';
    const res = await api.get('/my-schedule/pdf', { params: { lang: safeLang }, responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `almadina-schedule-${safeLang}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
  // In-app notification feed — reshape native Notification docs to the
  // legacy { id, category, date, source, url, ... } the UI was built on.
  getMyNotifications: async () => {
    const all = [];
    const limit = 100;
    let page = 1;
    let keepLoading = true;

    while (keepLoading) {
      const res = await api.get('/notifications', { params: { page, limit } });
      const list = res.data?.data || res.data?.notifications || [];
      all.push(...list);

      const pagination = res.data?.pagination || {};
      const totalPages = Number(pagination.totalPages || 0);
      const total = Number(pagination.total || 0);
      keepLoading = list.length === limit
        && (!totalPages || page < totalPages)
        && (!total || all.length < total);
      page += 1;
    }

    return { data: { success: true, data: all.map(_shapeNotificationForUi) } };
  }
};

export const teacherLmsService = {
  // Server-side /my-courses already filters to the teacher's courses and
  // returns them under `teaching` ([] in `enrolled`). Reshape to legacy.
  getCourses: async () => {
    const res = await api.get('/my-courses');
    const teaching = res.data?.teaching || [];
    const list = teaching.map((c) => ({ ...c, id: c._id || c.id }));
    return { data: { success: true, data: list } };
  },
  // Union of all students enrolled in every course the caller teaches.
  // Keeps the legacy flat-list shape for any callers that need it.
  getStudents: async () => {
    const groups = await teacherLmsService.getStudentsByCourse();
    const byId = new Map();
    for (const g of groups.data.data) {
      for (const s of g.students) {
        const id = String(s._id || s.id);
        if (!byId.has(id)) byId.set(id, s);
      }
    }
    return { data: { success: true, data: Array.from(byId.values()) } };
  },

  // Per-course breakdown — every course the teacher owns, paired with
  // its full enrolled-student roster. Lets the "My Students" page group
  // / filter by course instead of collapsing everything into one flat
  // list. Filters out non-student roles (teacher/admin) at this layer.
  getStudentsByCourse: async () => {
    const res = await api.get('/my-courses');
    const courses = res.data?.teaching || [];
    const groups = await Promise.all(courses.map(async (c) => {
      const cid = c._id || c.id;
      try {
        const r = await api.get(`/rosters/course/${cid}`);
        const members = r.data?.data?.members || r.data?.members || [];
        const students = members.filter(m =>
          String(m.role || 'student').toLowerCase() === 'student'
        );
        return {
          courseId: String(cid),
          courseName: c.fullname || c.name || c.shortname || 'مساق',
          students
        };
      } catch (e) {
        return {
          courseId: String(cid),
          courseName: c.fullname || c.name || c.shortname || 'مساق',
          students: [],
          error: e?.response?.data?.message || e.message
        };
      }
    }));
    return { data: { success: true, data: groups } };
  }
};

// Lead services
export const leadService = {
  getStatuses: () => api.get('/leads/statuses'),
  saveStatuses: (definitions, revision) => api.put('/leads/statuses', { definitions, revision }),
  getAll: (params = {}) => api.get('/leads', { params }),
  getOne: (id) => api.get(`/leads/${id}`),
  getEditFields: (id, params = {}) => api.get(`/leads/${id}/edit-fields`, { params }),
  history: (id, params = {}) => api.get(`/leads/${id}/history`, { params }),
  activitySummary: (leadIds) => api.post('/leads/activity-summary', { leadIds }),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  convert: (id, data = {}) => api.post(`/leads/${id}/convert`, data),
  convertBulkPreview: (data) => api.post('/leads/convert-bulk-preview', data),
  convertBulk: (data) => api.post('/leads/convert-bulk', data),
  previewDistribution: (data) => api.post('/leads/distribution/preview', data),
  executeDistribution: (data) => api.post('/leads/distribution/execute', data, { timeout: 0 }),
  addNote: (id, text) => api.post(`/leads/${id}/notes`, { text }),
  getStats: (params = {}) => api.get('/leads/stats', { params }),
  getGroups: (params) => api.get('/leads/groups', { params })
};

// Contact services — general address book (organize + message people who are
// neither leads nor students: partners, alumni, parents, vendors, community).
export const contactService = {
  importPreview: (form) => api.post('/contacts/import/preview', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  importCommit: (token) => api.post('/contacts/import/commit', { token, confirmed: true }, { timeout: 300000 }),
  export: (params = {}) => api.get('/contacts/export', { params, responseType: 'blob' }),
  getAll: (params = {}) => api.get('/contacts', { params }),
  getOne: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
  addNote: (id, text) => api.post(`/contacts/${id}/notes`, { text }),
  getStats: () => api.get('/contacts/stats'),
  getCategories: () => api.get('/contacts/categories'),
  // Google Contacts two-way sync (superadmin/admin/manager only).
  syncNow: () => api.post('/contacts/sync'),
  setSyncEnabled: (enabled) => api.put('/contacts/sync/settings', { enabled }),
  syncStatus: () => api.get('/contacts/sync/status')
};

// Deletion requests — pending-delete approval workflow. Staff/marketing
// deletes file a request; manager/admin/superadmin approve (runs the real
// delete) or reject. `pendingIds` powers the "قيد الحذف" row badges.
export const deletionRequestService = {
  list: (params = {}) => api.get('/deletion-requests', { params }),
  pendingIds: (resourceType) => api.get('/deletion-requests', { params: { idsOnly: 1, resourceType } }),
  approve: (id) => api.post(`/deletion-requests/${id}/approve`),
  reject: (id, reason = '') => api.post(`/deletion-requests/${id}/reject`, { reason }),
  cancel: (id) => api.delete(`/deletion-requests/${id}`)
};

// Payment services
export const paymentService = {
  getAll: (params) => api.get('/payments', { params }),
  // Downloads the filtered CSV through the authenticated axios client and
  // triggers a browser save. We can't use a plain <a href> because the
  // endpoint requires the JWT in the Authorization header, not a cookie.
  exportCsv: async (params) => {
    const res = await api.get('/payments/export.csv', { params, responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  getStats: () => api.get('/payments/stats'),
  getStudentPayments: (studentId) => api.get(`/payments/student/${studentId}`),
  getStudentSummary: (studentId, opts = {}) => {
    const q = new URLSearchParams();
    if (opts.year != null && opts.year !== '') q.set('year', String(opts.year));
    if (opts.cohortId) q.set('cohortId', String(opts.cohortId));
    const qs = q.toString();
    return api.get(`/payments/student/${studentId}/summary${qs ? `?${qs}` : ''}`);
  },
  // Bulk: one POST returns a map { [studentId]: summary } for an arbitrary
  // list of student ids. Optional year / cohortId scope it to a year-of-program
  // (multi-year cohorts). Used by the cohort Payments tab to avoid N requests.
  getBulkSummary: (studentIds, opts = {}) => api.post('/payments/bulk-summary', {
    studentIds,
    ...(opts.year != null && opts.year !== '' ? { year: opts.year } : {}),
    ...(opts.cohortId ? { cohortId: opts.cohortId } : {})
  }),
  // Full multi-cohort multi-year breakdown for the Student Financial 360 page.
  // One round-trip → cohorts[] (each with years[] containing courses, adjustments,
  // payments + computed totals) + an "unassigned" bucket + grand totals.
  getFinanceBreakdown: (studentId) => api.get(`/payments/student/${studentId}/finance-breakdown`),
  getSuspendedStudents: () => api.get('/payments/suspended-students'),
  getPendingApproval: () => api.get('/payments/pending-approval'),
  // Returns ledger rows plus `data.summary` ({ expected, paid, balance }), the
  // authoritative student-owned tuition aggregate. Consumers must not combine
  // that summary with plan installments.
  getMyPayments: () => api.get('/payments/my-payments'),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  // Void an incorrectly recorded payment; not deletion or an external refund.
  cancel: (id, reason) => api.post(`/payments/${encodeURIComponent(id)}/cancel`, { reason }),
  permanentDelete: (id, reason) => api.post(`/payments/${encodeURIComponent(id)}/permanent-delete`, { reason, confirmed: true }),
  uploadProof: (id, formData) => api.post(`/payments/${id}/upload-proof`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Fetch the proof file (image/PDF) as a Blob via the authenticated viewer
  // endpoint. Pop a new tab using a blob URL so the browser displays it
  // inline without exposing it publicly on Drive.
  viewProof: async (id) => {
    const res = await api.get(`/payments/${id}/proof`, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(res.data);
    const win = window.open(blobUrl, '_blank');
    // Free the object URL after the new tab has had time to load it.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    return win;
  },
  // Fetch the proof as a blob and return a usable object URL + mime type +
  // a revoke fn. Used by the inline split-pane viewer so the image/PDF
  // renders without ever leaking the file publicly. Caller must call
  // `revoke()` when the URL is no longer needed.
  getProofBlobUrl: async (id, { signal } = {}) => {
    const res = await api.get(`/payments/${id}/proof`, { responseType: 'blob', signal });
    const blob = res.data;
    const url = URL.createObjectURL(blob);
    return {
      url,
      mimeType: blob.type || 'application/octet-stream',
      revoke: () => URL.revokeObjectURL(url),
    };
  },
  approve: (id, notes) => api.post(`/payments/${id}/approve`, { notes }),
  reject: (id, reason) => api.post(`/payments/${id}/reject`, { reason }),
  retryPlanSync: (id) => api.post(`/payments/${id}/retry-plan-sync`),
  processReminders: () => api.post('/payments/process-reminders'),
  // Rich dashboard data (cards + 12mo revenue + top overdue + cohort balance).
  getDashboard: () => api.get('/payments/dashboard'),
  // Courses + programs a student can be charged against — drives the
  // required course/program picker in the Add-Payment dialog.
  getAttributionOptions: (studentId) => api.get(`/payments/student/${studentId}/attribution-options`),
  // PDF downloads — fetched as a blob through the authenticated client so the
  // JWT travels in the Authorization header, then opened in a new tab.
  _openPdf: async (path, filename) => {
    let res;
    try {
      res = await api.get(path, { responseType: 'blob' });
    } catch (error) {
      // Axios exposes JSON error bodies as Blob instances when the request
      // asked for a blob. Decode the server's message before handing the
      // error to the page so owner/RBAC failures are actionable.
      const body = error?.response?.data;
      if (body && typeof body.text === 'function') {
        try {
          const text = await body.text();
          try {
            const payload = JSON.parse(text);
            if (payload?.message) error.response.data = payload;
          } catch {
            if (text.trim()) error.response.data = { message: text.trim() };
          }
        } catch {
          // Preserve the original Axios error when the body is not JSON.
        }
      }
      throw error;
    }
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (!win) {
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
  downloadReceipt: function (id) { return this._openPdf(`/payments/${id}/receipt.pdf`, `receipt-${id}.pdf`); },
  downloadStatement: function (studentId) { return this._openPdf(`/payments/student/${studentId}/statement.pdf`, `statement-${studentId}.pdf`); },
  downloadMonthlyReport: function (month) { return this._openPdf(`/payments/report/monthly.pdf?month=${month}`, `payments-${month}.pdf`); }
};

// Notification services
export const notificationService = {
  getAll: (unreadOnly = false) => api.get('/payments/notifications', { params: { unreadOnly } }),
  markAsRead: (id) => api.put(`/payments/notifications/${id}/read`),
  markAllAsRead: () => api.put('/payments/notifications/read-all')
};

// Payment Plan services
export const paymentPlanService = {
  getAll: (params) => api.get('/payment-plans', { params }),
  getOne: (id) => api.get(`/payment-plans/${id}`),
  getMyPlans: () => api.get('/payment-plans/my-plans'),
  getStudentPlans: (studentId) => api.get(`/payment-plans/student/${studentId}`),
  create: (data) => api.post('/payment-plans', data),
  update: (id, data) => api.put(`/payment-plans/${id}`, data),
  delete: (id) => api.delete(`/payment-plans/${id}`),
  payInstallment: (planId, installmentId, data) => 
    api.post(`/payment-plans/${planId}/pay-installment/${installmentId}`, data),
  updateInstallment: (planId, installmentId, data) => 
    api.put(`/payment-plans/${planId}/installments/${installmentId}`, data),
  getStats: () => api.get('/payment-plans/stats/summary')
};

// Broadcast services
export const broadcastService = {
  getCampaignAnalytics: (id) => api.get(`/broadcast/campaigns/${id}/analytics`),
  retryFailed: (id, data) => api.post(`/broadcast/campaigns/${id}/retry-failed`, data),
  getCampaigns: (params = {}) => api.get('/broadcast/campaigns', { params }),
  getCampaign: (id, params = {}) => api.get(`/broadcast/campaigns/${id}`, { params }),
  getWhatsappBillingStatus: () => api.get('/broadcast/whatsapp-billing-status'),
  // Authenticated capacity contract for the compose estimate. The axios
  // interceptor attaches the current bearer token and the backend remains the
  // source of provider/account limits.
  getCapacity: () => api.get('/broadcast/capacity'),
  getWhatsappUsage: (month) => api.get('/broadcast/whatsapp-stats', { params: { month } }),
  createCampaign: (data) => api.post('/broadcast/campaigns', data),
  previewRecipients: (data) => api.post('/broadcast/preview-recipients', data),
  searchRecipients: (q) => api.get('/broadcast/recipient-search', { params: { q } }),
  listNewsletterSubscribers: (params = {}) => api.get('/broadcast/newsletter-subscribers', { params }),
  addNewsletterSubscriber: (data) => api.post('/broadcast/newsletter-subscribers', data),
  deleteNewsletterSubscriber: (id) => api.delete(`/broadcast/newsletter-subscribers/${id}`),
  sendNewsletter: (data) => api.post('/broadcast/newsletter', data),
  getStats: () => api.get('/broadcast/stats'),
  getTemplates: () => api.get('/broadcast/templates'),
  cancelCampaign: (id) => api.post(`/broadcast/campaigns/${id}/cancel`),
  pauseCampaign: (id) => api.post(`/broadcast/campaigns/${id}/pause`),
  resumeCampaign: (id) => api.post(`/broadcast/campaigns/${id}/resume`),
  retryBillingFailed: (id) => api.post(`/broadcast/campaigns/${id}/retry-billing-failed`, { confirm: true }),
  deleteCampaign: (id) => api.delete(`/broadcast/campaigns/${id}`)
};

export const whatsappHistoryService = {
  getMine: (params = {}) => api.get('/whatsapp-history/me', { params }),
  getUser: (userId, params = {}) => api.get(`/whatsapp-history/users/${userId}`, { params }),
};

// Native (mobile-app) push — device registry + announcements
export const nativePushService = {
  getStatus: () => api.get('/native-push/status'),
  broadcast: (data) => api.post('/native-push/broadcast', data),
  listAnnouncements: () => api.get('/native-push/announcements'),
};

// Email Designer — reusable rich-HTML email templates (drag-and-drop builder).
export const emailTemplateService = {
  list: (scope) => api.get('/email-templates', { params: scope ? { scope } : {} }),
  get: (id) => api.get(`/email-templates/${id}`),
  create: (data) => api.post('/email-templates', data),
  update: (id, data) => api.put(`/email-templates/${id}`, data),
  duplicate: (id) => api.post(`/email-templates/${id}/duplicate`),
  remove: (id) => api.delete(`/email-templates/${id}`)
};

// Cohort & Course rosters (read-only enrolment views)
export const rosterService = {
  cohort: (cohortId) => api.get(`/rosters/cohort/${cohortId}`),
  course: (courseId) => api.get(`/rosters/course/${courseId}`)
};

// In-app notification feed (bell icon, /notifications page)
export const searchService = {
  /** Global system-wide search. Returns grouped results filtered by role. */
  query: (q, limit = 5, scope) => api.get('/search', { params: { q, limit, ...(scope ? { scope } : {}) } }),
};

export const appNotificationService = {
  list: (params) => api.get('/notifications', { params }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all')
};

// User services
export const userService = {
  getAll: (params) => api.get('/users', { params }),
  getStudents: () => api.get('/users?role=student&limit=0'),
  getOne: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`)
};

export const studentNoteService = {
  list: (id) => api.get(`/users/${id}/notes`),
  add: (id, text, requestId) => api.post(`/users/${id}/notes`, { text, requestId }),
};

// Per-student price overrides — admin-only sub-resource of /users/:userId.
// Lists active overrides by default; pass status='all' to include cancelled
// & superseded rows for audit views.
export const priceOverrideService = {
  list:   (userId, params = {}) => api.get(`/users/${userId}/price-overrides`, { params }),
  create: (userId, data)        => api.post(`/users/${userId}/price-overrides`, data),
  cancel: (userId, overrideId)  => api.delete(`/users/${userId}/price-overrides/${overrideId}`),
};

// ------------------------------------------------------------
// Native admin LMS service — backs admin/Students, Teachers, Cohorts,
// Courses, and Leads. Calls portal-native endpoints (/users, /courses,
// /enrollments, /cohorts) and reshapes responses into the field names
// the legacy UI expects (id/firstname/lastname/cohorts/...).
// ------------------------------------------------------------

// Shape one User Mongo doc into the legacy field-name shape
// the admin pages were built around.
function _shapeUser(u, roleName) {
  const id = String(u._id || u.id);
  const fullName = u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim();
  const parts = fullName.split(/\s+/);
  return {
    id,
    _id: id,
    username: u.username,
    email: u.email,
    firstname: u.firstName || parts[0] || '',
    lastname: u.lastName || parts.slice(1).join(' ') || '',
    title: u.title || '',
    name: fullName,
    fullname: fullName,
    displayName: u.title ? `${u.title} ${fullName}`.trim() : fullName,
    phone: u.phone || '',
    // National ID (رقم الهوية) is the primary identifier. Prefer it, then fall
    // back to the internal student/teacher number for legacy records.
    idnumber: u.idNumber || u.studentId || u.teacherId || u.idnumber || '',
    idNumber: u.idNumber || '',
    // Staff-managed demographics. Must be shaped through explicitly or the
    // admin edit dialogs read `undefined` and silently blank the field on save.
    gender: u.gender || '',
    city: u.city || '',
    studentId: u.studentId || '',
    teacherId: u.teacherId || '',
    suspended: u.isActive === false || u.suspended === true,
    isActive: u.isActive !== false,
    whatsappEnabled: u.whatsappEnabled !== false,
    status: (u.isActive === false || u.suspended === true) ? 'suspended' : (u.status || 'active'),
    role: u.role?.name || u.role || roleName,
    cohorts: u.cohorts || [],
    cohortNames: (u.cohorts || []).map(c => c.name).filter(Boolean),
    // `courses` is the legacy field the admin Students UI reads from
    // (both the edit-dialog "existing courses" pre-check and the
    // view-dialog badge list). The backend hydrates these from active
    // student Enrollments in userService.listUsers / getUserById.
    enrolledCourses: u.enrolledCourses || [],
    courses: (u.enrolledCourses || []).map(c => ({
      id: String(c._id || c.id),
      _id: String(c._id || c.id),
      name: c.name || c.fullname || '',
      tag: c.tag || '',
    })),
    lastAccess: u.lastLoginAt || u.lastAccess || null,
    // One-shot credential fields returned by userService.createUser when
    // the server auto-generates a password. Surfaced to the admin "user
    // created" success dialog so they can copy/share the plaintext.
    // These are only present on the create response — never on list/get.
    temporaryPassword: u.temporaryPassword,
    credentialsEmailed: u.credentialsEmailed,
    credentialsEmailError: u.credentialsEmailError,
    enrollmentWarnings: u.enrollmentWarnings,
  };
}

function _shapeCourse(c) {
  return {
    id: String(c._id || c.id),
    _id: String(c._id || c.id),
    shortname: c.tag || c.shortname || '',
    fullname: c.name || c.fullname || '',
    summary: c.description || c.summary || '',
    visible: c.status !== 'archived',
    startdate: c.startDate || null,
    enddate: c.endDate || null,
    teacherName: c.teacherName || '',
    // Cohort metadata — used by the admin Courses + Teachers UIs to show
    // the cohort name below each course.
    cohortId: c.cohortId || null,
    cohortNumber: c.cohortNumber || 0,
    cohortName: c.cohortName || null,
    programName: c.programName || c.program?.name || null,
    programNumber: c.programNumber || 0,
    isStandalone: !!c.isStandalone
  };
}

// Tuition adjustments — discounts, scholarships, extra fees per
// (student × cohort × programYear). Admin/staff only on the server.
export const tuitionAdjustmentService = {
  list: (opts = {}) => {
    const q = new URLSearchParams();
    if (opts.student) q.set('student', opts.student);
    if (opts.cohortId) q.set('cohortId', opts.cohortId);
    if (opts.programYear != null) q.set('programYear', String(opts.programYear));
    const qs = q.toString();
    return api.get(`/tuition-adjustments${qs ? `?${qs}` : ''}`);
  },
  create: (data) => api.post('/tuition-adjustments', data),
  update: (id, data) => api.patch(`/tuition-adjustments/${id}`, data),
  remove: (id) => api.delete(`/tuition-adjustments/${id}`)
};

export const adminLmsService = {
  // ---- Students (User where role=student) -----------------------------
  getStudents: async () => {
    const res = await api.get('/users', { params: { role: 'student', limit: 0 } });
    const list = res.data?.data || res.data?.users || [];
    return { data: { success: true, data: list.map(u => _shapeUser(u, 'student')) } };
  },
  // Server-side paginated + searchable students list. Accepts
  // { page, limit, search, ...filters } and returns the legacy-shaped rows
  // alongside the pagination envelope from the server.
  listStudents: async (params = {}) => {
    const res = await api.get('/users', { params: { ...params, role: 'student' } });
    const list = res.data?.data || res.data?.users || [];
    return { data: { success: true, data: list.map(u => _shapeUser(u, 'student')), pagination: res.data?.pagination } };
  },
  getStudent: async (id) => {
    const res = await api.get(`/users/${id}`);
    return { data: { success: true, data: _shapeUser(res.data?.data || res.data, 'student') } };
  },
  createStudent: async (data) => {
    const payload = {
      username: data.username,
      email: data.email,
      name: `${data.firstname || ''} ${data.lastname || ''}`.trim() || data.username,
      firstName: data.firstname,
      lastName: data.lastname,
      phone: data.phone,
      // National ID only. The legacy internal studentId is NOT written from
      // this field so entering a real رقم الهوية never overwrites it.
      idNumber: data.idnumber,
      // Mandatory (certificate wording) — the server rejects creates without it.
      gender: data.gender || '',
      role: 'student',
      cohortIds: data.cohortIds || [],
      courseIds: data.courseIds || [],
      // Enroll ONLY the courses the admin ticked. The dialog pre-ticks a
      // picked group's courses (so the net enrollment matches the old
      // "auto-enroll the whole cohort" behaviour) but lets the admin untick
      // any before saving. Server keeps cohort membership but skips its
      // cohort→courses auto-enroll union so unticked courses never enroll/bill.
      enrollOnlySelectedCourses: true
    };
    const res = await api.post('/users', payload, { params: { impact: 1 } });
    return { data: { success: true, data: _shapeUser(res.data?.data || res.data, 'student'), financeImpactToken: res.data?.financeImpactToken } };
  },
  bulkCreateStudents: async (students, cohortIds = [], courseIds = []) => {
    // Single round-trip to the server. The server loops per-student via
    // userService.createUser so cohort wiring + course auto-enrollment
    // (union of cohort-linked + explicit courses) runs identically to
    // the single-create path. Per-row failures come back in `failed[]`
    // instead of aborting the batch.
    const res = await api.post('/users/bulk-students', {
      students,
      cohortIds,
      courseIds
    });
    const data = res.data?.data || res.data || {};
    return { data: { success: true, data: {
      created: data.created || [],
      failed:  data.failed  || [],
      flagged: data.flagged || [],
      success: data.success ?? (data.created?.length || 0),
      total:   data.total   ?? students.length
    } } };
  },
  useExistingBulkStudent: async (userId, cohortIds = [], courseIds = []) => {
    const res = await api.post('/users/bulk-students/use-existing', {
      userId,
      cohortIds,
      courseIds
    });
    return res.data;
  },
  updateStudent: async (id, data, opts = {}) => {
    // PATCH-like semantics over the existing PUT contract: omitted fields must
    // remain omitted (especially status and unrelated enrollment arrays).
    const payload = {};
    const fields = {
      username: 'username', email: 'email', firstname: 'firstName', lastname: 'lastName',
      phone: 'phone', idnumber: 'idNumber', gender: 'gender', city: 'city',
      referredBy: 'referredBy', role: 'role',
    };
    for (const [input, output] of Object.entries(fields)) {
      if (Object.hasOwn(data, input)) payload[output] = data[input];
    }
    if (Object.hasOwn(data, 'firstname') && Object.hasOwn(data, 'lastname')) {
      payload.name = `${data.firstname || ''} ${data.lastname || ''}`.trim();
    }
    if (Object.hasOwn(data, 'suspended')) payload.isActive = !data.suspended;
    for (const key of ['cohortIds', 'courseIds']) {
      if (Object.hasOwn(data, key) && data[key]?.length) payload[key] = data[key];
    }
    if (payload.cohortIds || payload.courseIds) payload.enrollOnlySelectedCourses = true;
    // Finance impact only on request (roster additions) — a plain profile
    // edit shouldn't pop a zero-change finance dialog.
    const res = await api.put(`/users/${id}`, payload, opts.impact ? { params: { impact: 1 } } : undefined);
    return { data: { success: true, data: _shapeUser(res.data?.data || res.data, 'student'), financeImpactToken: res.data?.financeImpactToken } };
  },
  deleteStudent: async (id) => {
    await api.delete(`/users/${id}`);
    return { data: { success: true } };
  },

  // ---- Teachers (same store as students, role=teacher) ----------------
  getTeachers: async () => {
    const res = await api.get('/users', { params: { role: 'teacher', limit: 0 } });
    const list = res.data?.data || res.data?.users || [];
    return { data: { success: true, data: list.map(u => _shapeUser(u, 'teacher')) } };
  },
  // Server-side paginated + searchable teachers list.
  listTeachers: async (params = {}) => {
    const res = await api.get('/users', { params: { ...params, role: 'teacher' } });
    const list = res.data?.data || res.data?.users || [];
    return { data: { success: true, data: list.map(u => _shapeUser(u, 'teacher')), pagination: res.data?.pagination } };
  },
  createTeacher: async (data) => {
    const payload = {
      username: data.username,
      email: data.email,
      name: `${data.firstname || ''} ${data.lastname || ''}`.trim() || data.username,
      title: data.title || '',
      firstName: data.firstname,
      lastName: data.lastname,
      phone: data.phone,
      // National ID only — never overwrite the legacy internal teacherId.
      idNumber: data.idnumber,
      gender: data.gender || '',
      city: data.city || '',
      role: 'teacher'
    };
    const res = await api.post('/users', payload);
    return { data: { success: true, data: _shapeUser(res.data?.data || res.data, 'teacher') } };
  },
  updateTeacher: async (id, data) => {
    const payload = {
      username: data.username,
      email: data.email,
      name: `${data.firstname || ''} ${data.lastname || ''}`.trim(),
      title: data.title || '',
      firstName: data.firstname,
      lastName: data.lastname,
      phone: data.phone,
      // National ID only — never overwrite the legacy internal teacherId.
      idNumber: data.idnumber,
      // Staff-only fields (never self-editable from the profile page).
      gender: data.gender || '',
      city: data.city || '',
      isActive: !data.suspended
    };
    const res = await api.put(`/users/${id}`, payload);
    // Sync the teacher's course assignments when courseIds were edited
    // alongside profile fields (the admin edit dialog ships them together).
    if (Array.isArray(data.courseIds)) {
      await api.put(`/users/${id}/teacher-courses`, { courseIds: data.courseIds });
    }
    return { data: { success: true, data: _shapeUser(res.data?.data || res.data, 'teacher') } };
  },
  getTeacherCourses: async (id) => {
    const res = await api.get(`/users/${id}/teacher-courses`);
    return { data: { success: true, data: res.data?.data || [] } };
  },
  deleteTeacher: async (id) => {
    await api.delete(`/users/${id}`);
    return { data: { success: true } };
  },

  // ---- Courses ---------------------------------------------------------
  getCourses: async () => {
    const res = await api.get('/courses');
    const list = res.data?.courses || res.data?.data || [];
    return { data: { success: true, data: list.map(_shapeCourse) } };
  },
  // Server-side paginated + searchable courses list.
  listCourses: async (params = {}) => {
    const res = await api.get('/courses', { params });
    const list = res.data?.courses || res.data?.data || [];
    return { data: { success: true, data: list.map(_shapeCourse), pagination: res.data?.pagination } };
  },
  createCourse: (data) => api.post('/courses', data),
  updateCourse: async (id, data) => {
    const payload = {
      name: data.fullname,
      tag: data.shortname,
      description: data.summary,
      startDate: data.startdate || null,
      endDate: data.enddate || null,
      status: data.visible === false ? 'archived' : 'active'
    };
    const res = await api.put(`/courses/${id}`, payload);
    return { data: { success: true, data: res.data } };
  },
  deleteCourse: async (id) => {
    await api.delete(`/courses/${id}`);
    return { data: { success: true } };
  },

  // ---- Course-level student management --------------------------------
  // Returns { isEnrolled } flag so the "Add students" dialog can
  // partition the list into already-enrolled vs available.
  getAvailableStudents: async (courseId) => {
    const [availRes, enrolledRes] = await Promise.all([
      api.get(`/enrollments/course/${courseId}/available`, { params: { limit: 1000 } }),
      api.get(`/enrollments/course/${courseId}`)
    ]);
    const available = (availRes.data?.data || []).map(u => ({
      ..._shapeUser(u, 'student'),
      isEnrolled: false
    }));
    const enrolled = (enrolledRes.data?.enrollments || [])
      .filter(e => e.role === 'student')
      .map(e => ({
        ..._shapeUser(e.user || {}, 'student'),
        isEnrolled: true
      }));
    return { data: { success: true, data: [...enrolled, ...available] } };
  },
  assignStudents: async (courseId, studentIds) => {
    const res = await api.post('/enrollments', { courseId, userIds: studentIds }, { params: { impact: 1 } });
    return { data: { success: true, financeImpactToken: res.data?.financeImpactToken } };
  },
  removeStudent: async (courseId, studentId, reason = '') => {
    const res = await api.delete('/enrollments', { data: { courseId, userId: studentId, reason }, params: { impact: 1 } });
    return { data: { success: true, financeImpactToken: res.data?.financeImpactToken } };
  },

  // ---- Cohorts (native /api/cohorts/* — Program subdocs) --------------
  getCohorts: () => api.get('/cohorts'),
  // Server-side paginated + searchable cohorts list. Returns the raw server
  // envelope ({ success, data, pagination }) — callers read res.data.data.
  listCohorts: (params = {}) => api.get('/cohorts', { params }),
  cohortStats: () => api.get('/cohorts/stats/summary'),
  createCohort: (data) => api.post('/cohorts', data),
  // Combined create+enrol — call the 3 native endpoints in sequence.
  createCohortWithMembers: async (data) => {
    const created = await api.post('/cohorts', {
      programId: data.programId,
      name: data.name,
      number: data.number,
      startDate: data.startDate,
      endDate: data.endDate
    });
    const cohort = created.data?.data;
    let coursesLinked = 0, membersAdded = 0;
    if (cohort?.id && Array.isArray(data.courseIds) && data.courseIds.length) {
      const r = await api.post(`/cohorts/${cohort.id}/courses`, { courseIds: data.courseIds });
      coursesLinked = r.data?.data?.linked || 0;
    }
    if (cohort?.id && Array.isArray(data.memberIds) && data.memberIds.length) {
      const r = await api.post(`/cohorts/${cohort.id}/members`, { userIds: data.memberIds });
      membersAdded = r.data?.data?.added || 0;
    }
    return { data: { success: true, data: { cohort, coursesLinked, membersAdded } } };
  },
  updateCohort: (id, data) => api.put(`/cohorts/${id}`, data),
  deleteCohort: (id) => api.delete(`/cohorts/${id}`),
  getCohortMembers: (id) => api.get(`/cohorts/${id}/members`),
  addCohortMembers: (id, userIds) =>
    api.post(`/cohorts/${id}/members`, { userIds }, { params: { impact: 1 } }),
  removeCohortMember: (cohortId, userId) =>
    api.delete(`/cohorts/${cohortId}/members/${userId}`, { params: { impact: 1 } }),
  getCohortCourses: (id) => api.get(`/cohorts/${id}/courses`),
  previewCohortCourseLink: (id, courseIds = [], options = {}) =>
    api.post(`/cohorts/${id}/courses/preview`, { courseIds, ...options }),
  linkCohortCourses: (id, courseIds, options = {}) =>
    api.post(`/cohorts/${id}/courses`, { courseIds, ...options }),
  unlinkCohortCourse: (cohortId, courseId) =>
    api.delete(`/cohorts/${cohortId}/courses/${courseId}`),
  // Assign a linked course to a specific year-of-program (1-based).
  // Used by multi-year cohorts (durationYears > 1) so the Payments tab
  // can group by year. Upserts a CohortCourse row if only a tagged
  // Course.cohortId existed before.
  setCohortCourseYear: (cohortId, courseId, programYear) =>
    api.patch(`/cohorts/${cohortId}/courses/${courseId}`, { programYear }),
  testConnection: () => Promise.resolve({ data: { success: true } })
};

// ------------------------------------------------------------
// Website Manager (CMS) — admin/staff/manager edit ALL the marketing
// website content from inside the portal. Backed by /api/cms which mutates
// the same models the public /api/public/site read API serves.
// `resource` is one of: departments | news | banners | testimonials |
//                       book-categories | books | pages
// ------------------------------------------------------------
export const cmsService = {
  list:   (resource, params = {}) => api.get(`/cms/${resource}`, { params }),
  create: (resource, data)      => api.post(`/cms/${resource}`, data),
  update: (resource, id, data)  => api.put(`/cms/${resource}/${id}`, data),
  remove: (resource, id)        => api.delete(`/cms/${resource}/${id}`),
  // Global site settings singleton (nav, top bar, contact, social, footer, SEO).
  getSiteSettings:    ()        => api.get('/cms/site-settings'),
  updateSiteSettings: (data)    => api.put('/cms/site-settings', data),
  getSubmissionDigestStatus: () => api.get('/cms/site-settings/digest'),
  sendSubmissionDigest: () => api.post('/cms/site-settings/digest/send'),
  resolveSubmissionDigestAttempt: (attemptId, data) => api.post(`/cms/site-settings/digest/attempts/${encodeURIComponent(attemptId)}/resolve`, data),
  // In-portal library — published books for ALL authenticated roles, each
  // annotated with { mine, canManage } so the UI can gate edit/delete.
  library:            (params = {}) => api.get('/cms/library', { params }),
  // Upload a marketing image (department/news/banner cover, book cover, ...).
  // Returns { success, url }. Accepts a File/Blob.
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append('image', file);
    return api.post('/cms/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Upload a book file (PDF/EPUB/Office doc) to S3. Returns { success, url }.
  uploadFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/cms/upload-file', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const displayPlaylistService = {
  list: () => api.get('/cms/display-playlists'),
  create: (data) => api.post('/cms/display-playlists', data),
  update: (id, data) => api.put(`/cms/display-playlists/${id}`, data),
  remove: (id) => api.delete(`/cms/display-playlists/${id}`),
  uploadPhotos: (id, files) => {
    const form = new FormData();
    Array.from(files || []).forEach((file) => form.append('images', file));
    return api.post(`/cms/display-playlists/${id}/photos`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updatePhoto: (id, photoId, data) =>
    api.patch(`/cms/display-playlists/${id}/photos/${photoId}`, data),
  removePhoto: (id, photoId) =>
    api.delete(`/cms/display-playlists/${id}/photos/${photoId}`),
  reorderPhotos: (id, photoIds) =>
    api.put(`/cms/display-playlists/${id}/photos/reorder`, { photoIds }),
  publicGet: (identifier) =>
    api.get(`/public/display-playlists/${encodeURIComponent(identifier)}`, {
      headers: { 'Cache-Control': 'no-cache' },
      params: { _: Date.now() },
    }),
};

// Website registration forms + cohorts + submissions (nested resources, so
// they have their own router at /api/cms/website-forms instead of the flat
// generic /api/cms registry).
export const websiteFormsService = {
  listForms:   ()          => api.get('/cms/website-forms'),
  createForm:  (data)      => api.post('/cms/website-forms', data),
  updateForm:  (id, data)  => api.put(`/cms/website-forms/${id}`, data),
  removeForm:  (id)        => api.delete(`/cms/website-forms/${id}`),

  listCohorts:  (formId)        => api.get(`/cms/website-forms/${formId}/cohorts`),
  createCohort: (formId, data)  => api.post(`/cms/website-forms/${formId}/cohorts`, data),
  updateCohort: (id, data)      => api.put(`/cms/website-forms/cohorts/${id}`, data),
  removeCohort: (id)            => api.delete(`/cms/website-forms/cohorts/${id}`),

  listRegistrations: (formId, cohortId) =>
    api.get(`/cms/website-forms/${formId}/registrations`, { params: cohortId && cohortId !== 'all' ? { cohortId } : {} }),
  updateRegistrationStatus: (id, status) =>
    api.put(`/cms/website-forms/registrations/${id}/status`, { status }),
  removeRegistration: (id) => api.delete(`/cms/website-forms/registrations/${id}`),
};

// Program/Course website-field editing. Scoped to website fields only
// (image, slug, isPublished, departments) and open to the office tier incl.
// manager — separate from the core program/course admin routes. `type` is
// 'programs' or 'courses'. Shaped to match the { data: { data: [...] } } /
// { data: {...} } envelope the website entity dialogs expect.
export const websiteEntityService = (type) => ({
  list: () => api.get(`/cms/entity/${type}`),
  update: (id, data) => api.put(`/cms/entity/${type}/${id}`, data),
});

// Faculty marketing profiles reuse the existing User store + /users routes.
// We only edit the website-facing fields here (title, bios, specialization,
// publish flag, ordering); identity/role are managed on the Teachers page.
export const facultyService = {
  // Raw teacher docs (NOT _shapeUser) so the website-facing fields
  // (bioAr/bioEn/specialization/publishToWebsite/facultyOrder/avatar) survive.
  list: async () => {
    const res = await api.get('/users', { params: { role: 'teacher', limit: 0 } });
    const list = res.data?.data || res.data?.users || [];
    return { data: { success: true, data: list } };
  },
  // Raw single teacher doc (NOT _shapeUser) so the website-facing fields survive
  // when opening the per-row Website profile dialog from the Teachers page.
  getOne: async (id) => {
    const res = await api.get(`/users/${id}`);
    return res.data?.data || res.data;
  },
  updateProfile: (id, data) => api.put(`/users/${id}`, {
    title: data.title,
    titleEn: data.titleEn,
    nameEn: data.nameEn,
    // `departments` is the manual multi-select; the server derives the legacy
    // single `department` from the first entry. `programs` are the programs this
    // teacher teaches — their departments also count toward the teacher's faculties.
    departments: data.departments,
    programs: data.programs,
    bioAr: data.bioAr,
    bioEn: data.bioEn,
    specializationAr: data.specializationAr,
    specializationEn: data.specializationEn,
    researchAr: data.researchAr,
    researchEn: data.researchEn,
    educationAr: data.educationAr,
    educationEn: data.educationEn,
    publishToWebsite: data.publishToWebsite,
    facultyOrder: data.facultyOrder,
    avatar: data.avatar,
  }),
};

// Styled RTL Excel (.xlsx) exports → saved to a Google Drive "exports" folder.
// scope: 'full' | 'program' | 'course'  (+ programId / courseId when scoped)
export const exportService = {
  modules: ()                 => api.get('/exports/modules'),
  run:     (module, data = {}) => api.post(`/exports/${module}`, data),
};

// Public, no-auth read API (same endpoints the website consumes). Used by the
// in-portal News & Events and Library browse pages so students/teachers see
// exactly what is published to the website. Plain fetch bypasses the bearer
// interceptor (these endpoints are public).
export const publicSiteService = {
  news:           ()  => fetch('/api/public/site/news').then(r => r.json()),
  events:         ()  => fetch('/api/public/site/events').then(r => r.json()),
  books:          ()  => fetch('/api/public/site/books').then(r => r.json()),
  bookCategories: ()  => fetch('/api/public/site/book-categories').then(r => r.json()),
  // Form-driven course catalogue (the same set shown on the marketing site):
  // a course only appears here while it has ≥1 active, non-deleted
  // registration form — delete/deactivate the form and it disappears.
  // Throw on non-2xx so callers show a real error state (not a false "empty").
  courses:        ()  => fetch('/api/public/site/courses').then(r => { if (!r.ok) throw new Error(`http_${r.status}`); return r.json(); }),
  settings:       ()  => fetch('/api/public/site/settings').then(r => { if (!r.ok) throw new Error(`http_${r.status}`); return r.json(); }),
};

export const surveyService = {
  list: () => api.get('/surveys', { withCredentials: true }),
  get: (id) => api.get(`/surveys/${encodeURIComponent(id)}`, { withCredentials: true }),
  create: (data) => api.post('/surveys', data, { withCredentials: true }),
  update: (id, data) => api.patch(`/surveys/${encodeURIComponent(id)}`, data, { withCredentials: true }),
  remove: (id) => api.delete(`/surveys/${encodeURIComponent(id)}`, { withCredentials: true }),
  results: (id, page = 1) => api.get(`/surveys/${encodeURIComponent(id)}/results`, { params: { page }, withCredentials: true }),
  publicGet: (slug) => api.get(`/public/surveys/${encodeURIComponent(slug)}`),
  submit: (slug, data) => api.post(`/public/surveys/${encodeURIComponent(slug)}`, data),
};

export default api;
